<template>
  <div ref="rootRef" class="version-filter">
    <div
      v-for="dim in VERSION_DIMENSIONS"
      :key="dim.key"
      class="version-filter__item"
      :class="{ 'is-open': openKey === dim.key, 'is-active': isDimActive(dim.key) }"
    >
      <button
        type="button"
        class="version-filter__trigger"
        :disabled="!hasAvailableOption(dim)"
        aria-haspopup="listbox"
        :aria-expanded="openKey === dim.key"
        @click="toggle(dim.key)"
      >
        <span class="version-filter__label">{{ dim.label }}</span>
        <span class="version-filter__value">{{ displayValue(dim) }}</span>
        <span class="version-filter__caret" aria-hidden="true" />
      </button>
      <div v-if="openKey === dim.key" class="version-filter__panel" role="listbox">
        <button
          type="button"
          class="version-filter__option"
          :class="{ 'is-selected': !isDimActive(dim.key) }"
          @click="pick(dim.key, null)"
        >
          すべて
        </button>
        <!-- 選択肢は全量表示。その日・その地域で上映が無いものは押せないだけで、隠さない -->
        <button
          v-for="opt in optionsOf(dim)"
          :key="`${dim.key}-${opt.versionId}`"
          type="button"
          class="version-filter__option"
          :class="{ 'is-selected': selectedIdOf(dim.key) === opt.versionId }"
          :disabled="opt.available === false"
          :title="opt.available === false ? '該当する上映がありません' : undefined"
          @click="pick(dim.key, opt.versionId)"
        >
          {{ opt.name }}
        </button>
      </div>
    </div>

    <button
      v-if="hasAnyActive"
      type="button"
      class="version-filter__reset"
      @click="resetAll"
    >
      <i class="fas fa-times" aria-hidden="true" />
      条件クリア
    </button>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { VERSION_DIMENSIONS } from '@/utils/dashboard-cinema-rank.js'

const props = defineProps({
  /**
   * 選択状態。各次元は配列（バックエンドが配列を受けるため）だが、
   * 仕様上 UI は単一選択なので要素は 0 個か 1 個のみ。
   */
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  /** pickVersionOptions() の戻り値。versionList / version1List / ... を持つ */
  options: {
    type: Object,
    default: () => ({}),
  },
})
const emit = defineEmits(['update:modelValue', 'change'])

const rootRef = ref(null)
const openKey = ref(null)

function optionsOf(dim) {
  return props.options?.[dim.optionsKey] ?? []
}

/**
 * その次元に「選べる」選択肢が1つでもあるか。
 * バックエンドは全量を返すようになったので、件数ではなく available で判定しないと
 * 全部グレーの次元まで開けてしまう
 */
function hasAvailableOption(dim) {
  return optionsOf(dim).some((opt) => opt.available !== false)
}

/** その次元で選択中の versionId（未選択なら null） */
function selectedIdOf(key) {
  const arr = props.modelValue?.[key]
  return Array.isArray(arr) && arr.length ? String(arr[0]) : null
}

function isDimActive(key) {
  return selectedIdOf(key) != null
}

const hasAnyActive = computed(() => VERSION_DIMENSIONS.some((dim) => isDimActive(dim.key)))

function displayValue(dim) {
  const id = selectedIdOf(dim.key)
  if (id == null) return 'すべて'
  const hit = optionsOf(dim).find((opt) => opt.versionId === id)
  return hit?.name ?? id
}

function toggle(key) {
  openKey.value = openKey.value === key ? null : key
}

function pick(key, versionId) {
  openKey.value = null
  const next = { ...props.modelValue }
  // 未選択（すべて）は空配列。undefined ではなく空配列で統一しておくと
  // 呼び出し側でキーの有無を気にせずに済む
  next[key] = versionId == null ? [] : [versionId]
  emit('update:modelValue', next)
  emit('change', next)
}

function resetAll() {
  openKey.value = null
  const next = { ...props.modelValue }
  VERSION_DIMENSIONS.forEach((dim) => {
    next[dim.key] = []
  })
  emit('update:modelValue', next)
  emit('change', next)
}

function onPointerDownOutside(event) {
  if (openKey.value == null) return
  const root = rootRef.value
  if (root && !root.contains(event.target)) {
    openKey.value = null
  }
}

function close() {
  openKey.value = null
}

onMounted(() => {
  document.addEventListener('pointerdown', onPointerDownOutside)
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onPointerDownOutside)
})

defineExpose({ close })
</script>

<style lang="less" scoped>
.version-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.version-filter__item {
  position: relative;
}

.version-filter__trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 15px;
  background: var(--mc-bg, #fff);
  color: var(--mc-text, #000);
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--mc-accent, #f9da49);
    background: var(--mc-row-hover, #fff);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.version-filter__label {
  color: var(--mc-text-muted, #4a4a4a);
  font-size: 11px;
}

.version-filter__value {
  font-weight: 600;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.version-filter__item.is-active .version-filter__trigger {
  border-color: var(--mc-accent, #f9da49);
  background: color-mix(in srgb, var(--mc-accent, #f9da49) 20%, var(--mc-bg, #fff));
}

.version-filter__caret {
  width: 5px;
  height: 5px;
  border-top: 1px solid currentColor;
  border-left: 1px solid currentColor;
  transform: scaleY(-1) rotate(45deg);
  opacity: 0.6;
  transition: transform 0.2s ease;
}

.version-filter__item.is-open .version-filter__caret {
  transform: rotate(45deg);
}

.version-filter__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  min-width: 140px;
  max-height: 280px;
  padding: 4px;
  overflow-y: auto;
  background: var(--mc-bg, #fff);
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 10px;
  box-shadow: 0 6px 20px rgb(0 0 0 / 12%);
}

.version-filter__option {
  display: block;
  width: 100%;
  padding: 7px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--mc-text, #000);
  font-size: 12px;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--mc-row-hover, #f5f5f5);
  }

  &.is-selected {
    background: var(--mc-row-selected, #fdf3c9);
    font-weight: 600;
  }

  /* 該当する上映が無い版本：表示はするが選べない */
  &:disabled {
    color: var(--mc-text-muted, #8a8a8a);
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.version-filter__reset {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border: 1px dashed var(--mc-border, #e5e5e5);
  border-radius: 15px;
  background: transparent;
  color: var(--mc-text-muted, #4a4a4a);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: var(--mc-text, #000);
    border-color: var(--mc-accent, #f9da49);
  }
}

@media (max-width: 768px) {
  /* 横スクロールだと隠れた次元に気付けないので、2列×2行に折り返して4次元とも常に見せる。
     地域フィルタは cinema-content 側で version-filter の上段に積まれるため、
     ここは単独の行として幅いっぱいに広がる前提でよい */
  .version-filter {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    width: 100%;
  }

  .version-filter__trigger {
    width: 100%;
    justify-content: center;
    padding: 0 8px;
  }

  .version-filter__value {
    max-width: 100px;
  }

  /* 「条件クリア」は 2 列グリッドの中で 1 行使い切り、他の行と同じく右端に揃える */
  .version-filter__reset {
    grid-column: 1 / -1;
    justify-self: end;
  }
}
</style>
